import { inject, Injectable } from "@angular/core";
import Crypto from "crypto-js";
import { lastValueFrom } from "rxjs";
import { UserBackend } from "src/app/backend/api/user.backend";

const DEMO_PLAINTEXT: string = "pin-check";

export interface PinChallenge {
  encryptedData: ArrayBuffer;
  salt: string;
}

@Injectable()
export class PinValidationService {
  private readonly userBackend = inject(UserBackend);

  async generateChallenge(): Promise<PinChallenge> {
    const { otp, salt } = await lastValueFrom(this.userBackend.generateOtp());
    const encryptedData: ArrayBuffer = await this.encryptData(
      DEMO_PLAINTEXT,
      otp,
      salt
    );

    return { encryptedData, salt };
  }

  async validatePin(pin: string, challenge: PinChallenge): Promise<boolean> {
    try {
      const decrypted: string = await this.decryptData(
        pin,
        challenge.salt,
        challenge.encryptedData
      );

      return decrypted === DEMO_PLAINTEXT;
    } catch {
      return false;
    }
  }

  private async encryptData(
    data: string,
    otp: string,
    salt: string
  ): Promise<ArrayBuffer> {
    const cryptoKey: CryptoKey = await this.generateCryptoKey(otp, salt);

    return await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: new ArrayBuffer(16),
        tagLength: 128
      },
      cryptoKey,
      new TextEncoder().encode(data)
    );
  }

  private async decryptData(
    pin: string,
    salt: string,
    encryptedData: ArrayBuffer
  ): Promise<string> {
    const otp: string = Crypto.algo.SHA256.create()
      .update(pin)
      .update(salt)
      .finalize()
      .toString(Crypto.enc.Base64);
    const cryptoKey: CryptoKey = await this.generateCryptoKey(otp, salt);
    const decBuffer: ArrayBuffer = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: new ArrayBuffer(16),
        tagLength: 128
      },
      cryptoKey,
      encryptedData
    );

    return new TextDecoder().decode(decBuffer);
  }

  private async generateCryptoKey(
    otp: string,
    salt: string
  ): Promise<CryptoKey> {
    const keyToDerive: CryptoKey = await window.crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(otp),
      "PBKDF2",
      false,
      ["deriveKey"]
    );

    return await window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: new TextEncoder().encode(salt),
        iterations: 250000,
        hash: "SHA-256"
      },
      keyToDerive,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
  }
}
